import { useDisclosure } from '@chakra-ui/react';
import React, { ChangeEvent, useEffect, useState } from 'react';

import {
  CmBox,
  CmButton,
  CmCheckBox,
  CmFlex,
  CmFormControl,
  CmFormErrorMessage,
  CmFormLabel,
  CmHeading,
  CmHelpterText,
  CmLabelAndInput,
  CmLabelAndSelect,
  CmLabelAndSingleReactSelect,
  CmLabelAndText,
  CmLabelAndTextarea,
  CmLabelWithQuestionIconAndInput,
  CmLabelWithQuestionIconAndSingleReactSelect,
  CmLabelWithQuestionIconAndText,
  CmLongLoading,
  CmNextLink,
  CmRadio,
  CmRadioGroup,
  CmSelect,
  CmStack,
  CmSwitch,
  CmText,
  ReactSingleSelectInput,
  SelectForm,
} from '@/components/uiParts';
import { EllipsesButton } from '@/components/uiParts/Button/EllipsesButton';
import {
  deliveryServiceIdMap,
  deliveryServiceNameMap,
} from '@/domain/models/DeliveryService';
import {
  DELIVERY_METHOD,
  DeliveryMethod,
  deliveryMethod,
  deliveryMethods,
} from '@/domain/models/Order/deliveryMethod';
import {
  ORDER_METHOD,
  OrderMethod,
  orderMethod,
  orderMethods,
} from '@/domain/models/Order/orderMethod';
import { errorHandler } from '@/lib/ErrorHandler';
import { HttpError } from '@/lib/exception';
import { calcMinTime } from '@/pages/menu-managements/executions/service';
import { SidebarWrapper } from '@/pages/store-management/components/Layout/SidebarWrapper';
import { DeliveryStoreIntegrationModal } from '@/pages/store-management/master-stores/components/Modal/DeliveryStoreIntegrationModal';
import {
  CAMEL_CALL_DELIVERY_STORE,
  CAMEL_ORDER_DELIVERY_STORE,
  CamelOrderPropField,
  CamelOrderPropValue,
  COOKING_TIMES_BY_CAMEL_CALL,
  COOKING_TIMES_BY_CAMEL_ORDER,
  DEMAECAN_DELIVERY_STORE,
  EatInReceiveMethod,
  EatInSettings,
  INITIAL_EXTERNAL_AUTH_DELIVERY_STORE,
  InitialDeliveryStore,
  MENU_DELIVERY_STORE,
  TAKEOUT_DAYS_OFFSET_MAX,
  TAKEOUT_END_DAYS_OFFSET_DEFAULT,
  TAKEOUT_START_DAYS_OFFSET_DEFAULT,
  UBER_DELIVERY_STORE,
} from '@/pages/store-management/master-stores/components/Sidebar/MasterStoreSidebarForm/service';
import { TitleLine } from '@/pages/store-management/master-stores/components/Sidebar/MasterStoreSidebarForm/TitleLine';
import {
  validateCamelOrderPropInput,
  validateCamelOrderStoreInput,
  validateMenuDeliveryStoreInput,
  validateUberDeliveryStoreInput,
} from '@/pages/store-management/master-stores/components/Sidebar/MasterStoreSidebarForm/validate';
import {
  ConnectDeliveryService,
  IntegrationDeliveryServiceName,
  integrationStatusMap,
  masterStoreForm,
  noRequest,
  nowRequest,
} from '@/pages/store-management/master-stores/service';
import { MasterStoreFormError } from '@/pages/store-management/master-stores/validate';
import { useStoreManagementDeliveryStoreActivationJobCreate } from '@/services/StoreManagement/DeliveryStoreActivationJobs/hooks/create/useStoreManagementDeliveryStoreActivationJobCreate';
import { useStoreManagementDeliveryStoreActivationJobLatest } from '@/services/StoreManagement/DeliveryStoreActivationJobs/hooks/latest/useStoreManagementDeliveryStoreActivationJobLatest';
import { useStoreManagementDeliveryStoreActivationJobUpdate } from '@/services/StoreManagement/DeliveryStoreActivationJobs/hooks/update/useStoreManagementDeliveryStoreActivationJobUpdate';
import {
  StoreManagementDeliveryStoreCreateParams,
  useStoreManagementDeliveryStoreCreate,
} from '@/services/StoreManagement/DeliveryStores/hooks/create/useStoreManagementDeliveryStoreCreate';
import {
  StoreManagementDeliveryStoreUpdateParams,
  useStoreManagementDeliveryStoreUpdate,
} from '@/services/StoreManagement/DeliveryStores/hooks/update/useStoreManagementDeliveryStoreUpdate';
import { ListIntegrationReadyDeliveryStoreResponse } from '@/services/StoreManagement/IntegrationReadyDeliveryStore/hooks/useListIntegrationReadyDeliveryStore';
import {
  DeliveryArea,
  LinkedStatus,
  StoreManagementMasterStoreDeliveryStore,
  StoreManagementMasterStoreShowReqponse,
} from '@/services/StoreManagement/MasterStores/hooks/show/useStoreManagementMasterStoreShow';
import { StoreManagementMasterStoreUpdateParams } from '@/services/StoreManagement/MasterStores/hooks/update/useStoreManagementMasterStoreUpdate';
import { useStoreManagementMasterStoreUpdateLinkedStatus } from '@/services/StoreManagement/MasterStores/hooks/UpdateLinkedStatus/useStoreManagementMasterStoreUpdateLinkedStatus';

import { formatAuthStoreIdText } from './formatAuthStoreIdText';

const PREFIX = 'sidebar-';

const CREATE_DELIVERY_STORE_MESSAGE =
  'のデリバリーサービス連携情報を登録しました。\n引き続き連携日時の設定を行なってください。';

const findDeliveryStore = (
  deliveryStores: StoreManagementMasterStoreDeliveryStore[],
  deliveryService: ConnectDeliveryService
) => {
  return deliveryStores.find(
    (ds) => ds.delivery_service_name === deliveryService
  );
};

type Props = {
  error: MasterStoreFormError;
  masterStoreName: string;
  masterStoreBusinessNumber: string;
  masterStoreBusinessName: string;
  sidebarMasterStoreId: number;
  isSidebarOpen: boolean;
  associateBrandSelectInput: ReactSingleSelectInput;
  authStoreSelectInput: ReactSingleSelectInput;
  targetMasterStore?: StoreManagementMasterStoreShowReqponse;
  hasCamelOrderSite: boolean;
  hasCamelCall: boolean;
  isAvailableEatin: boolean;
  setMasterStoreName: React.Dispatch<React.SetStateAction<string>>;
  setMasterStoreBusinessNumber: React.Dispatch<React.SetStateAction<string>>;
  setMasterStoreBusinessName: React.Dispatch<React.SetStateAction<string>>;
  setHasCamelOrderSite: React.Dispatch<React.SetStateAction<boolean>>;
  setHasCamelCall: React.Dispatch<React.SetStateAction<boolean>>;
  handleCloseSidebar: () => void;
  handleInputMasterStoreName: (e: ChangeEvent<HTMLInputElement>) => void;
  handleInputMasterStoreBusinessNumber: (
    e: ChangeEvent<HTMLInputElement>
  ) => void;
  handleInputMasterStoreBusinessName: (
    e: ChangeEvent<HTMLInputElement>
  ) => void;
  handleUpdateStore: (
    params: StoreManagementMasterStoreUpdateParams
  ) => Promise<void>;
  handleDeleteStore: () => Promise<void>;
  refetchMasterStoresAndSet: () => Promise<void>;
  integrationReadyDeliveryStores: ListIntegrationReadyDeliveryStoreResponse[];
};

export const MasterStoreEditSidebarForm: React.FC<Props> = ({
  error,
  sidebarMasterStoreId,
  masterStoreName,
  masterStoreBusinessNumber,
  masterStoreBusinessName,
  isSidebarOpen,
  associateBrandSelectInput,
  authStoreSelectInput,
  targetMasterStore,
  hasCamelOrderSite,
  hasCamelCall,
  isAvailableEatin,
  setMasterStoreName,
  setMasterStoreBusinessNumber,
  setMasterStoreBusinessName,
  setHasCamelOrderSite,
  setHasCamelCall,
  handleCloseSidebar,
  handleInputMasterStoreName,
  handleInputMasterStoreBusinessNumber,
  handleInputMasterStoreBusinessName,
  handleUpdateStore,
  handleDeleteStore,
  refetchMasterStoresAndSet,
  integrationReadyDeliveryStores,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [integrationDate, setIntegrationDate] = useState<Date>(calcMinTime(10));
  const [integrationJobId, setIntegrationJobId] = useState<string>('');
  const [uberDeliveryStore, setUberDeliveryStore] =
    useState<InitialDeliveryStore>({ ...UBER_DELIVERY_STORE });
  const [menuDeliveryStore, setMenuDeliveryStore] =
    useState<InitialDeliveryStore>({ ...MENU_DELIVERY_STORE });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [demaecanDeliveryStore, setDemaecanDeliveryStore] =
    useState<InitialDeliveryStore>({
      ...DEMAECAN_DELIVERY_STORE,
    });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [camelOrderDeliveryStore, setCamelOrderDeliveryStore] =
    useState<InitialDeliveryStore>({
      ...CAMEL_ORDER_DELIVERY_STORE,
    });
  const [camelCallDeliveryStore, setCamelCallDeliveryStore] =
    useState<InitialDeliveryStore>({
      ...CAMEL_CALL_DELIVERY_STORE,
    });
  const [linkedStatus, setLinkedStatus] = useState<LinkedStatus>('enabled');
  const [
    targetDeliveryStoreDeliveryService,
    setTargetDeliveryStoreDeliveryService,
  ] = useState<ConnectDeliveryService>('Uber Eats');
  const {
    isOpen: isOpenIntegrationModal,
    onClose: handelCloseIntegrationModal,
    onOpen: handelOpenIntegrationModal,
  } = useDisclosure();
  const [pickupNotesError, setPickupNotesError] = useState<string>('');
  const [tableNoPlaceholderError, setTableNoPlaceholderError] =
    useState<string>('');
  const [
    uberIntegrationReadyDeliveryStoreOptions,
    setUberIntegrationReadyDeliveryStoreOptions,
  ] = useState<SelectForm[]>([]);
  const [
    targetIntegrationReadyDeliveryStoreSelectForSidebar,
    setTargetIntegrationReadyDeliveryStoreSelectForSidebar,
  ] = useState<SelectForm | null>({ label: '', value: '' });

  const handleOpenModalAndSetDeliveryStore = async (
    deliveryService: ConnectDeliveryService
  ) => {
    const targetDeliveryStore = deliveryStoreMap[deliveryService]['store'];
    const nowAfterTenMinutes = calcMinTime(10);
    if (targetDeliveryStore.integration_status === noRequest) {
      setIntegrationDate(nowAfterTenMinutes);
    } else {
      const res = await useStoreManagementDeliveryStoreActivationJobLatest({
        delivery_store_id: targetDeliveryStore.id,
      });
      if (res !== null) {
        setIntegrationJobId(res.id);
        setIntegrationDate(new Date(res.integration_date));
      } else {
        setIntegrationDate(nowAfterTenMinutes);
      }
    }
    setTargetDeliveryStoreDeliveryService(deliveryService);
    handelOpenIntegrationModal();
  };
  const linkedStatusMap = (
    {
      disabled: {
        text: 'タブレットで非表示中',
        color: 'red',
        onChange: async () => {
          await useStoreManagementMasterStoreUpdateLinkedStatus(
            sidebarMasterStoreId,
            {
              linked_status: 'enabled',
            }
          );
          setLinkedStatus('enabled');
          alert('タブレットで表示にするように更新しました。');
        },
      },
      enabled: {
        text: 'タブレットで表示中',
        color: '#319795',
        onChange: async () => {
          await useStoreManagementMasterStoreUpdateLinkedStatus(
            sidebarMasterStoreId,
            {
              linked_status: 'disabled',
            }
          );
          alert('タブレットで非表示にするように更新しました。');
          setLinkedStatus('disabled');
        },
      },
    } as const
  )[linkedStatus];

  const deliveryStoreMap = {
    'Uber Eats': {
      store: uberDeliveryStore,
      text: 'は現在時間以降の日時を指定してください。',
      deliveryServiceName: deliveryServiceNameMap['Uber Eats'],
      setStore: (ds: InitialDeliveryStore) => setUberDeliveryStore(ds),
    },
    menu: {
      store: menuDeliveryStore,
      text: 'は連携に最低7営業日以降の日時を指定してください。',
      deliveryServiceName: deliveryServiceNameMap['menu'],
      setStore: (ds: InitialDeliveryStore) => setMenuDeliveryStore(ds),
    },
    Demaecan: {
      store: demaecanDeliveryStore,
      text: 'は出前館社と同意した連携日をご記入ください',
      deliveryServiceName: deliveryServiceNameMap['Demaecan'],
      setStore: (ds: InitialDeliveryStore) => setDemaecanDeliveryStore(ds),
    },
    CamelOrder: {
      store: camelOrderDeliveryStore,
      text: 'は店舗を掲載する掲載日時を指定してください。',
      deliveryServiceName: deliveryServiceNameMap['CamelOrder'],
      setStore: (ds: InitialDeliveryStore) => setCamelOrderDeliveryStore(ds),
    },
    CamelCall: {
      store: camelCallDeliveryStore,
      text: 'は店舗を掲載する掲載日時を指定してください。',
      deliveryServiceName: deliveryServiceNameMap['CamelCall'],
      setStore: (ds: InitialDeliveryStore) => setCamelCallDeliveryStore(ds),
    },
  } as const;

  const targetDeliveryStore =
    deliveryStoreMap[targetDeliveryStoreDeliveryService];

  const isDisabledIntegrationUpdateButton =
    integrationStatusMap[targetDeliveryStore.store.integration_status] !==
    '申請中';

  const isDisabledCamelOrderDeliveryStoreUpdateButton = () => {
    const camel_order_prop = camelOrderDeliveryStore.camel_order_prop;
    return (
      camel_order_prop === null ||
      camel_order_prop.order_methods.length === 0 || // 注文方式が何も選択されていない
      (camel_order_prop.order_methods.includes(ORDER_METHOD.DELIVERY) && // 注文方式でデリバリーが選択されているが、配達方式が選択されていない
        camel_order_prop.delivery_methods.length === 0) ||
      tableNoPlaceholderError !== ''
    );
  };

  const isDisabledCamelCallDeliveryStoreUpdateButton = () => {
    const prop = camelCallDeliveryStore.camel_order_prop;
    return (
      prop === null ||
      prop.order_methods.length === 0 || // 注文方式が何も選択されていない
      (prop.order_methods.includes(ORDER_METHOD.DELIVERY) && // 注文方式でデリバリーが選択されているが、配達方式が選択されていない
        prop.delivery_methods.length === 0)
    );
  };

  const deliveryAreaNames = (deliveryAreas: DeliveryArea[] | undefined) => {
    return deliveryAreas?.map((area) => area.name).join('、');
  };

  const handleChangeCamelOrderCookingTime = (
    e: ChangeEvent<HTMLSelectElement>
  ) => {
    const copyCamelOrderDeliveryStore = { ...camelOrderDeliveryStore };
    copyCamelOrderDeliveryStore.cooking_time =
      e.target.value === '' ? null : Number(e.target.value);
    setCamelOrderDeliveryStore(copyCamelOrderDeliveryStore);
  };

  const handleChangeCamelCallCookingTime = (
    e: ChangeEvent<HTMLSelectElement>
  ) => {
    const copyCamelCallDeliveryStore = { ...camelCallDeliveryStore };
    copyCamelCallDeliveryStore.cooking_time =
      e.target.value === '' ? null : Number(e.target.value);
    setCamelCallDeliveryStore(copyCamelCallDeliveryStore);
  };

  const handleChangeCamelOrderProp = (
    propField: CamelOrderPropField,
    value: CamelOrderPropValue<CamelOrderPropField>
  ) => {
    const copyCamelOrderDeliveryStore = { ...camelOrderDeliveryStore };
    if (copyCamelOrderDeliveryStore.camel_order_prop === null) {
      copyCamelOrderDeliveryStore.camel_order_prop = {
        takeout_start_days_offset: 0,
        takeout_end_days_offset: TAKEOUT_END_DAYS_OFFSET_DEFAULT,
        order_methods: [],
        delivery_methods: [],
        pickup_notes: '',
        is_asap_displayed: true,
      };
    }
    if (
      propField === 'takeout_start_days_offset' ||
      propField === 'takeout_end_days_offset'
    ) {
      if (typeof value === 'number' || value === null) {
        copyCamelOrderDeliveryStore.camel_order_prop[propField] = value;
      }
    } else if (propField === 'order_methods') {
      copyCamelOrderDeliveryStore.camel_order_prop[propField] =
        value as OrderMethod[];
    } else if (propField === 'delivery_methods') {
      copyCamelOrderDeliveryStore.camel_order_prop[propField] =
        value as DeliveryMethod[];
    } else if (propField === 'pickup_notes') {
      copyCamelOrderDeliveryStore.camel_order_prop[propField] = value as string;
    } else if (propField === 'is_asap_displayed') {
      copyCamelOrderDeliveryStore.camel_order_prop[propField] =
        value as boolean;
    } else if (propField === 'eat_in_settings') {
      copyCamelOrderDeliveryStore.camel_order_prop[propField] =
        value as EatInSettings | null;
    }
    setCamelOrderDeliveryStore(copyCamelOrderDeliveryStore);
  };

  const handleChangeCamelCallProp = (
    propField: CamelOrderPropField,
    value: CamelOrderPropValue<CamelOrderPropField>
  ) => {
    const copyCamelCallDeliveryStore = { ...camelCallDeliveryStore };
    if (copyCamelCallDeliveryStore.camel_order_prop === null) {
      copyCamelCallDeliveryStore.camel_order_prop = {
        takeout_start_days_offset: 0,
        takeout_end_days_offset: TAKEOUT_END_DAYS_OFFSET_DEFAULT,
        order_methods: [],
        delivery_methods: [],
        pickup_notes: '',
        is_asap_displayed: true,
      };
    }
    if (propField === 'order_methods') {
      copyCamelCallDeliveryStore.camel_order_prop[propField] =
        value as OrderMethod[];
    } else if (propField === 'delivery_methods') {
      copyCamelCallDeliveryStore.camel_order_prop[propField] =
        value as DeliveryMethod[];
    }
    setCamelCallDeliveryStore(copyCamelCallDeliveryStore);
  };

  const handleChangeTakoutStartDaysOffset = (
    e: ChangeEvent<HTMLSelectElement>
  ) => {
    handleChangeCamelOrderProp('takeout_start_days_offset', +e.target.value);
  };

  const handleChangeTakoutEndDaysOffset = (
    e: ChangeEvent<HTMLSelectElement>
  ) => {
    handleChangeCamelOrderProp('takeout_end_days_offset', +e.target.value);
  };

  const handleChangePickupNotes = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length > 200) {
      setPickupNotesError('200文字以内で入力してください。');
    } else {
      setPickupNotesError('');
      handleChangeCamelOrderProp('pickup_notes', value);
    }
  };

  const handleChangeEatInReceiveMethod = (e: ChangeEvent<HTMLInputElement>) => {
    const currentProp = camelOrderDeliveryStore.camel_order_prop;
    const isChecked = e.target.checked;
    const value = e.target.value as EatInReceiveMethod;
    const currentSettings = currentProp?.eat_in_settings ?? {
      receive_methods: [],
      table_no_placeholder: null,
    };
    const currentMethods = currentSettings.receive_methods;

    const newMethods = isChecked
      ? [...currentMethods, value]
      : currentMethods.filter((method) => method !== value);

    const newSettings: EatInSettings = {
      ...currentSettings,
      receive_methods: newMethods,
    };

    // TABLE_DELIVERYがOFFになった場合、プレースホルダーをクリア
    if (!newMethods.includes('TABLE_DELIVERY')) {
      newSettings.table_no_placeholder = null;
    }

    handleChangeCamelOrderProp(
      'eat_in_settings',
      newMethods.length > 0 ? newSettings : null
    );
  };

  const handleChangeTableNoPlaceholder = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length > 20) {
      setTableNoPlaceholderError('20文字以内で入力してください。');
      return;
    }
    setTableNoPlaceholderError('');
    const currentProp = camelOrderDeliveryStore.camel_order_prop;
    const currentSettings = currentProp?.eat_in_settings ?? {
      receive_methods: [],
      table_no_placeholder: null,
    };

    const newSettings: EatInSettings = {
      ...currentSettings,
      table_no_placeholder: value || null,
    };

    handleChangeCamelOrderProp('eat_in_settings', newSettings);
  };

  const isEatInOrderMethodSelected =
    camelOrderDeliveryStore.camel_order_prop?.order_methods?.includes(
      ORDER_METHOD.EAT_IN
    );
  const isTableDeliveryChecked =
    camelOrderDeliveryStore.camel_order_prop?.eat_in_settings?.receive_methods?.includes(
      'TABLE_DELIVERY'
    ) ?? false;

  const isCounterPickupChecked =
    camelOrderDeliveryStore.camel_order_prop?.eat_in_settings?.receive_methods?.includes(
      'COUNTER_PICKUP'
    ) ?? false;

  const handleChangeOrderMethod = (e: ChangeEvent<HTMLInputElement>) => {
    const currentProp = camelOrderDeliveryStore.camel_order_prop;
    const isChecked = e.target.checked;
    const value = e.target.value as OrderMethod;
    const currentMethods = currentProp?.order_methods ?? [];

    const newValues = isChecked
      ? [...currentMethods, value]
      : currentMethods.filter((method) => method !== value);

    if (!newValues.includes(ORDER_METHOD.DELIVERY)) {
      handleChangeCamelOrderProp('delivery_methods', []);
    }

    handleChangeCamelOrderProp('order_methods', newValues);
  };

  const handleChangeCamelCallOrderMethod = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const currentProp = camelCallDeliveryStore.camel_order_prop;
    const isChecked = e.target.checked;
    const value = e.target.value as OrderMethod;
    const currentMethods = currentProp?.order_methods ?? [];

    const newValues = isChecked
      ? [...currentMethods, value]
      : currentMethods.filter((method) => method !== value);

    if (!newValues.includes(ORDER_METHOD.DELIVERY)) {
      handleChangeCamelCallProp('delivery_methods', []);
    } else {
      handleChangeCamelCallProp('delivery_methods', [
        DELIVERY_METHOD.DELIVERY_BY_STORE,
      ]);
    }

    handleChangeCamelCallProp('order_methods', newValues);
  };

  const isDeliveryAreaEmpty = targetMasterStore?.delivery_areas?.length === 0;

  const isDisabledCamelOrderDeliveryMethodCheckButton = (
    value: DeliveryMethod
  ) => {
    // 注文方式でデリバリーが選択されていない場合はチェックボックスを無効化
    if (
      !camelOrderDeliveryStore.camel_order_prop?.order_methods?.includes(
        ORDER_METHOD.DELIVERY
      )
    ) {
      return true;
    }
    // 配達方式で自社配達が選択されている場合は、配達エリアが未設定の場合はチェックボックスを無効化
    if (value === DELIVERY_METHOD.DELIVERY_BY_STORE) {
      if (isDeliveryAreaEmpty) {
        return true;
      }
    }
    return false;
  };

  const isDisabledCamelCallDeliveryMethodCheckButton = (
    value: DeliveryMethod
  ) => {
    // 注文方式でデリバリーが選択されていない場合はチェックボックスを無効化
    if (
      !camelCallDeliveryStore.camel_order_prop?.order_methods?.includes(
        ORDER_METHOD.DELIVERY
      )
    ) {
      return true;
    }
    // 配達方式で自社配達が選択されている場合は、配達エリアが未設定の場合はチェックボックスを無効化
    if (value === DELIVERY_METHOD.DELIVERY_BY_STORE) {
      if (isDeliveryAreaEmpty) {
        return true;
      }
    }
    return false;
  };

  const isDeliveryMethodChecked = (optionValue: DeliveryMethod): boolean => {
    // 配達方式で自社配達が選択されている場合は、配達エリアが未設定の場合はチェックボックスを無効化
    if (
      optionValue === DELIVERY_METHOD.DELIVERY_BY_STORE &&
      isDeliveryAreaEmpty
    ) {
      return false;
    }

    return (
      camelOrderDeliveryStore.camel_order_prop?.delivery_methods?.includes(
        optionValue
      ) ?? false
    );
  };

  const handleChangeDeliveryMethod = (e: ChangeEvent<HTMLInputElement>) => {
    const currentProp = camelOrderDeliveryStore.camel_order_prop;
    const isChecked = e.target.checked;
    const value = e.target.value as DeliveryMethod;
    const currentMethods = currentProp?.delivery_methods ?? [];
    const newValues = isChecked
      ? [...currentMethods, value]
      : currentMethods.filter((method) => method !== value);

    handleChangeCamelOrderProp('delivery_methods', newValues);
  };

  const handleChangeCamelCallDeliveryMethod = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const currentProp = camelCallDeliveryStore.camel_order_prop;
    const isChecked = e.target.checked;
    const value = e.target.value as DeliveryMethod;
    const currentMethods = currentProp?.delivery_methods ?? [];
    const newValues = isChecked
      ? [...currentMethods, value]
      : currentMethods.filter((method) => method !== value);

    if (newValues.length === 0) {
      // 配達方式が全て未選択の場合、注文方式からDELIVERYのみを除外
      const currentOrderMethods = currentProp?.order_methods ?? [];
      const updatedOrderMethods = currentOrderMethods.filter(
        (method) => method !== ORDER_METHOD.DELIVERY
      );
      handleChangeCamelCallProp('order_methods', updatedOrderMethods);
    }

    handleChangeCamelCallProp('delivery_methods', newValues);
  };

  const handleInputUberPassword = (e: ChangeEvent<HTMLInputElement>) => {
    const copyUberDeliveryStore = { ...uberDeliveryStore };
    if (copyUberDeliveryStore.external_auth_delivery_store === null) return;
    copyUberDeliveryStore.external_auth_delivery_store.password =
      e.target.value;
    setUberDeliveryStore(copyUberDeliveryStore);
  };

  const handleInputUberEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    const copyUberDeliveryStore = { ...uberDeliveryStore };
    if (copyUberDeliveryStore.external_auth_delivery_store === null) return;
    copyUberDeliveryStore.external_auth_delivery_store.email = e.target.value;
    setUberDeliveryStore(copyUberDeliveryStore);
  };

  const handleInputUberStoreInfo = (
    integrationReadyDeliveryStore: SelectForm
  ) => {
    const copyUberDeliveryStore = { ...uberDeliveryStore };
    copyUberDeliveryStore.external_store_id =
      integrationReadyDeliveryStore.value ?? '';
    copyUberDeliveryStore.external_store_name =
      integrationReadyDeliveryStore.label ?? '';
    setUberDeliveryStore(copyUberDeliveryStore);
  };

  const handleInputMenuStoreId = (e: React.ChangeEvent<HTMLInputElement>) => {
    const copyMenuDeliveryStore = { ...menuDeliveryStore };
    copyMenuDeliveryStore.external_store_id = e.target.value;
    setMenuDeliveryStore(copyMenuDeliveryStore);
  };

  const handleCreateDeliveryStore = async (
    params: StoreManagementDeliveryStoreCreateParams
  ) => {
    setIsLoading(true);
    try {
      const res = await useStoreManagementDeliveryStoreCreate(params);
      await refetchMasterStoresAndSet();
      setIsLoading(false);
      return res;
    } catch (error) {
      setIsLoading(false);
      if (error instanceof HttpError) {
        if (
          error.status === 400 ||
          error.status === 401 ||
          error.status === 409
        ) {
          alert(error.message), setIsLoading(false);
          return;
        }
      }
      // @ts-ignore TS2322
      return errorHandler({ error });
    }
  };

  const handleUpdateDeliveryStore = async (
    id: string,
    params: StoreManagementDeliveryStoreUpdateParams,
    deliveryServiceName: IntegrationDeliveryServiceName
  ) => {
    setIsLoading(true);
    try {
      const res = await useStoreManagementDeliveryStoreUpdate(id, params);
      alert(
        `${deliveryServiceName}のデリバリーサービス連携情報を更新しました。`
      );
      await refetchMasterStoresAndSet();
      setIsLoading(false);
      return res;
    } catch (error) {
      setIsLoading(false);
      if (error instanceof HttpError) {
        if (
          error.status === 400 ||
          error.status === 401 ||
          error.status === 409
        ) {
          alert(error.message), setIsLoading(false);
          return;
        }
      }
      // @ts-ignore TS2322
      return errorHandler({ error });
    }
  };

  const setDeliveryStoreId = (
    id: string,
    deliveryStore: InitialDeliveryStore,
    setDeliveryStore: (
      value: React.SetStateAction<InitialDeliveryStore>
    ) => void
  ) => {
    const copyDeliveryStore = { ...deliveryStore };
    copyDeliveryStore.id = id;
    setDeliveryStore(copyDeliveryStore);
  };

  const camelOrderHandler = {
    create: async () => {
      if (camelOrderDeliveryStore.external_auth_delivery_store === null) return;
      if (validateCamelOrderStoreInput(camelOrderDeliveryStore.cooking_time)) {
        return;
      }
      if (
        validateCamelOrderPropInput(camelOrderDeliveryStore.camel_order_prop)
      ) {
        return;
      }
      const res = await handleCreateDeliveryStore({
        master_store_id: sidebarMasterStoreId,
        external_store_id: '',
        delivery_service_id: deliveryServiceIdMap['CamelOrder'],
        external_auth_delivery_store: {
          username: '',
          password: '',
          email: '',
        },
        camel_order_cooking_time: camelOrderDeliveryStore.cooking_time,
        camel_order_prop: camelOrderDeliveryStore.camel_order_prop,
      });
      if (res === undefined) return;
      handleOpenModalAndSetDeliveryStore('CamelOrder');
      setIntegrationDate(calcMinTime(10));
      setDeliveryStoreId(
        res.id,
        camelOrderDeliveryStore,
        setCamelOrderDeliveryStore
      );
    },
    update: async () => {
      if (camelOrderDeliveryStore.external_auth_delivery_store === null) return;
      const { id } = camelOrderDeliveryStore.external_auth_delivery_store;
      if (validateCamelOrderStoreInput(camelOrderDeliveryStore.cooking_time)) {
        return;
      }
      if (
        validateCamelOrderPropInput(camelOrderDeliveryStore.camel_order_prop)
      ) {
        return;
      }
      await handleUpdateDeliveryStore(
        camelOrderDeliveryStore.id,
        {
          external_store_id: camelOrderDeliveryStore.external_store_id,
          delivery_service_id: deliveryServiceIdMap['CamelOrder'],
          external_auth_delivery_store: {
            id,
            email: '',
            password: '',
            username: '',
          },
          cooking_time: camelOrderDeliveryStore.cooking_time,
          camel_order_prop: camelOrderDeliveryStore.camel_order_prop,
        },
        'CamelOrder'
      );
    },
  };

  const camelCallHandler = {
    create: async () => {
      if (camelCallDeliveryStore.external_auth_delivery_store === null) return;
      if (validateCamelOrderStoreInput(camelCallDeliveryStore.cooking_time)) {
        return;
      }
      if (
        validateCamelOrderPropInput(camelCallDeliveryStore.camel_order_prop)
      ) {
        return;
      }
      const res = await handleCreateDeliveryStore({
        master_store_id: sidebarMasterStoreId,
        external_store_id: '',
        delivery_service_id: deliveryServiceIdMap['CamelCall'],
        external_auth_delivery_store: {
          username: '',
          password: '',
          email: '',
        },
        camel_order_cooking_time: camelCallDeliveryStore.cooking_time,
        camel_order_prop: camelCallDeliveryStore.camel_order_prop,
      });
      if (res === undefined) return;
      handleOpenModalAndSetDeliveryStore('CamelCall');
      setIntegrationDate(calcMinTime(10));
      setDeliveryStoreId(
        res.id,
        camelCallDeliveryStore,
        setCamelCallDeliveryStore
      );
    },
    update: async () => {
      if (camelCallDeliveryStore.external_auth_delivery_store === null) return;
      const { id } = camelCallDeliveryStore.external_auth_delivery_store;
      if (validateCamelOrderStoreInput(camelCallDeliveryStore.cooking_time)) {
        return;
      }
      await handleUpdateDeliveryStore(
        camelCallDeliveryStore.id,
        {
          external_store_id: camelCallDeliveryStore.external_store_id,
          delivery_service_id: deliveryServiceIdMap['CamelCall'],
          external_auth_delivery_store: {
            id,
            email: '',
            password: '',
            username: '',
          },
          cooking_time: camelCallDeliveryStore.cooking_time,
          camel_order_prop: camelCallDeliveryStore.camel_order_prop,
        },
        'CamelCall'
      );
    },
  };

  const uberHandler = {
    create: async () => {
      if (uberDeliveryStore.external_auth_delivery_store === null) return;
      const { ...deliveryStoreInput } =
        uberDeliveryStore.external_auth_delivery_store;
      const { email, password } = deliveryStoreInput;
      if (
        validateUberDeliveryStoreInput(
          email,
          password,
          uberDeliveryStore.external_store_id
        )
      )
        return;
      const res = await handleCreateDeliveryStore({
        master_store_id: sidebarMasterStoreId,
        external_store_id:
          targetIntegrationReadyDeliveryStoreSelectForSidebar?.value ?? '',
        delivery_service_id: deliveryServiceIdMap['Uber Eats'],
        external_auth_delivery_store: { email, password, username: '' },
        camel_order_cooking_time: null,
        camel_order_prop: null,
      });
      if (res === undefined) return;
      alert(`Uber Eats${CREATE_DELIVERY_STORE_MESSAGE}`);
      setTargetDeliveryStoreDeliveryService('Uber Eats');
      setIntegrationDate(calcMinTime(10));
      setDeliveryStoreId(res.id, uberDeliveryStore, setUberDeliveryStore);
    },
    update: async () => {
      if (uberDeliveryStore.external_auth_delivery_store === null) return;
      const { id, email, password } =
        uberDeliveryStore.external_auth_delivery_store;
      if (
        validateUberDeliveryStoreInput(
          email,
          password,
          uberDeliveryStore.external_store_id
        )
      )
        return;
      await handleUpdateDeliveryStore(
        uberDeliveryStore.id,
        {
          external_store_id: uberDeliveryStore.external_store_id,
          delivery_service_id: deliveryServiceIdMap['Uber Eats'],
          external_auth_delivery_store: { id, email, password, username: '' },
          cooking_time: uberDeliveryStore.cooking_time,
          camel_order_prop: null,
        },
        'Uber Eats'
      );
    },
  };

  const menuHandler = {
    update: async () => {
      if (menuDeliveryStore.external_auth_delivery_store === null) return;
      if (validateMenuDeliveryStoreInput(menuDeliveryStore.external_store_id))
        return;
      const { id } = menuDeliveryStore.external_auth_delivery_store;
      await handleUpdateDeliveryStore(
        menuDeliveryStore.id,
        {
          external_store_id: menuDeliveryStore.external_store_id,
          delivery_service_id: deliveryServiceIdMap['menu'],
          external_auth_delivery_store: {
            id,
            email: '',
            password: '',
            username: '',
          },
          cooking_time: menuDeliveryStore.cooking_time,
          camel_order_prop: null,
        },
        'menu'
      );
    },
  };

  const demaeCanHandler = {
    create: async () => {
      if (demaecanDeliveryStore.external_auth_delivery_store === null) return;
      const res = await handleCreateDeliveryStore({
        master_store_id: sidebarMasterStoreId,
        external_store_id: '',
        delivery_service_id: deliveryServiceIdMap['Demaecan'],
        external_auth_delivery_store: {
          username: '',
          password: '',
          email: '',
        },
        camel_order_cooking_time: null,
        camel_order_prop: null,
      });
      if (res === undefined) return;
      alert(`出前館${CREATE_DELIVERY_STORE_MESSAGE}`);
      handleOpenModalAndSetDeliveryStore('Demaecan');
      setIntegrationDate(calcMinTime(10));
      setDeliveryStoreId(
        res.id,
        demaecanDeliveryStore,
        setDemaecanDeliveryStore
      );
    },
  };

  const setDeliveryStore = (
    deliveryStore: StoreManagementMasterStoreDeliveryStore | undefined,
    INITIAL_DELIVERY_STORE: InitialDeliveryStore,
    setter: (value: React.SetStateAction<InitialDeliveryStore>) => void
  ) => {
    if (deliveryStore !== undefined) {
      const copyDs = { ...deliveryStore };
      if (copyDs.external_auth_delivery_store === null) {
        if (INITIAL_EXTERNAL_AUTH_DELIVERY_STORE === null) return;
        copyDs.external_auth_delivery_store = {
          ...INITIAL_EXTERNAL_AUTH_DELIVERY_STORE,
        };
      }
      setter(copyDs);
    } else {
      const copyDs = { ...INITIAL_DELIVERY_STORE };
      setter(copyDs);
    }
  };

  const handleInputIntegrationDate = (date: Date) => {
    setIntegrationDate(date);
  };

  const handleChangeTime = (
    time: string,
    targetMethod: 'setHours' | 'setMinutes'
  ) => {
    const copyNewTime = integrationDate;
    const newTargetTime = new Date(copyNewTime.getTime());
    newTargetTime[targetMethod](Number(time));
    setIntegrationDate(newTargetTime);
  };

  const handleCreateIntegration = async () => {
    setIsLoading(true);
    try {
      await useStoreManagementDeliveryStoreActivationJobCreate({
        delivery_store_id: targetDeliveryStore.store.id,
        integration_date: integrationDate,
      });
      alert(`${targetDeliveryStore.deliveryServiceName}の申請を登録しました。`);
      const newTargetDeliveryStore = { ...targetDeliveryStore };
      newTargetDeliveryStore.store.integration_status = nowRequest;
      targetDeliveryStore.setStore(newTargetDeliveryStore.store);
      await refetchMasterStoresAndSet();
      setIsLoading(false);
      handelCloseIntegrationModal();
    } catch (error) {
      if (error instanceof HttpError && error.status === 400) {
        setIsLoading(false);
        return alert(error.message);
      }
      setIsLoading(false);
      // @ts-ignore TS2322
      errorHandler({ error });
    }
  };

  const handleUpdateIntegration = async () => {
    setIsLoading(true);
    try {
      await useStoreManagementDeliveryStoreActivationJobUpdate(
        integrationJobId,
        {
          integration_date: integrationDate,
        }
      );
      setIsLoading(false);
      alert(`${targetDeliveryStore.deliveryServiceName}の申請を更新しました。`);
      handelCloseIntegrationModal();
    } catch (error) {
      if (error instanceof HttpError && error.status === 400) {
        setIsLoading(false);
        return alert(error.message);
      }
      setIsLoading(false);
      // @ts-ignore TS2322
      errorHandler({ error });
    }
  };

  const uberIntegrationReadyDeliveryStoreSelectInput: ReactSingleSelectInput = {
    value: targetIntegrationReadyDeliveryStoreSelectForSidebar,
    options: uberIntegrationReadyDeliveryStoreOptions,
    onChange: async (integrationReadyDeliveryStore: SelectForm | null) => {
      if (
        integrationReadyDeliveryStore === null ||
        integrationReadyDeliveryStore.value ===
          targetIntegrationReadyDeliveryStoreSelectForSidebar?.value
      )
        return;
      setTargetIntegrationReadyDeliveryStoreSelectForSidebar(
        integrationReadyDeliveryStore
      );
      handleInputUberStoreInfo(integrationReadyDeliveryStore);
    },
    onReset: () =>
      setTargetIntegrationReadyDeliveryStoreSelectForSidebar({
        value: '',
        label: '',
      }),
  };

  const initializeEditState = async (
    masterStore: StoreManagementMasterStoreShowReqponse
  ) => {
    if (sidebarMasterStoreId === 0) return;
    setIsLoading(true);
    setMasterStoreName(masterStore.name);
    setMasterStoreBusinessNumber(masterStore.business_number ?? '');
    setMasterStoreBusinessName(masterStore.business_name ?? '');
    setLinkedStatus(masterStore.linked_status);
    associateBrandSelectInput.onChange({
      label: masterStore.brand_name,
      value: 'dummy-value',
    });
    setHasCamelOrderSite(masterStore.has_camel_order_site);
    setHasCamelCall(masterStore.has_camel_call);
    authStoreSelectInput.onChange({
      label: masterStore.auth_store.name,
      value: masterStore.auth_store.id.toString(),
    });
    const uberDs = findDeliveryStore(masterStore.delivery_stores, 'Uber Eats');
    setDeliveryStore(uberDs, UBER_DELIVERY_STORE, setUberDeliveryStore);
    const menuDs = findDeliveryStore(masterStore.delivery_stores, 'menu');
    setDeliveryStore(menuDs, MENU_DELIVERY_STORE, setMenuDeliveryStore);
    const demaecanDs = findDeliveryStore(
      masterStore.delivery_stores,
      'Demaecan'
    );
    setDeliveryStore(
      demaecanDs,
      DEMAECAN_DELIVERY_STORE,
      setDemaecanDeliveryStore
    );
    const camelOrderDs = findDeliveryStore(
      masterStore.delivery_stores,
      'CamelOrder'
    );
    setDeliveryStore(
      camelOrderDs,
      CAMEL_ORDER_DELIVERY_STORE,
      setCamelOrderDeliveryStore
    );
    const camelCallDs = findDeliveryStore(
      masterStore.delivery_stores,
      'CamelCall'
    );
    setDeliveryStore(
      camelCallDs,
      CAMEL_CALL_DELIVERY_STORE,
      setCamelCallDeliveryStore
    );
    const uberIntegrationReadyDeliveryStoreOptions =
      integrationReadyDeliveryStores
        .filter(
          (i) => i.delivery_service_id === deliveryServiceIdMap['Uber Eats']
        )
        .map<SelectForm>((i) => ({
          value: i.external_store_id,
          label: i.external_store_name,
        }));
    setUberIntegrationReadyDeliveryStoreOptions(
      uberIntegrationReadyDeliveryStoreOptions
    );
    uberIntegrationReadyDeliveryStoreSelectInput.onReset();
    setIsLoading(false);
  };

  useEffect(() => {
    (async () => {
      if (targetMasterStore === undefined) return;
      await initializeEditState(targetMasterStore);
    })();
  }, [targetMasterStore]);

  const VerticalSeparate = ({ margin }: { margin: string }) => (
    <CmBox
      borderBottom="2px solid #bbb"
      height="0.5px"
      width="100%"
      {...{ margin }}
    />
  );
  const camelOrderDateOffsetOptions = Array.from(
    { length: TAKEOUT_DAYS_OFFSET_MAX + 1 },
    (_, i) => (
      <option key={i} value={i}>
        {i}日後
      </option>
    )
  );

  const isTakeoutOrderMethodSelected =
    camelOrderDeliveryStore.camel_order_prop?.order_methods?.includes(
      ORDER_METHOD.TAKE_OUT
    );

  // is_asap_displayed のデフォルト値が Trueなので、camel_order_propが nullish な場合は asap を返す
  // 存在する場合は実際に値を参照して asap, scheduled を返す
  const isAsapDisplayed =
    camelOrderDeliveryStore.camel_order_prop == null ||
    camelOrderDeliveryStore.camel_order_prop == undefined
      ? 'asap'
      : camelOrderDeliveryStore.camel_order_prop.is_asap_displayed
      ? 'asap'
      : 'scheduled';

  const authStoreIdText = formatAuthStoreIdText(
    targetMasterStore?.auth_store?.id
  );

  return (
    <>
      <SidebarWrapper
        {...{
          isSidebarOpen,
          handleCloseSidebar,
          buttonComponent: (
            <EllipsesButton
              menus={[
                {
                  label: '削除する',
                  menuItemProps: {
                    _hover: { bg: 'rgba(0, 0, 0, 0.06)' },
                    onClick: handleDeleteStore,
                    fontWeight: 'bold',
                    color: 'red',
                  },
                },
              ]}
              menuButtonProps={{
                _hover: { bg: 'rgba(0, 0, 0, 0.06)' },
                padding: '5px 10px',
              }}
            />
          ),
        }}
      >
        <CmBox>
          <CmBox>
            <CmFlex flexDirection="row-reverse">
              <CmFormControl mt="10px" display="flex" alignItems="center">
                <CmFormLabel
                  mb="0"
                  mr="10px"
                  color={linkedStatusMap.color}
                  htmlFor="linkedStatus"
                  fontWeight={'bold'}
                >
                  {linkedStatusMap.text}
                </CmFormLabel>
                <CmSwitch
                  id="linkedStatus"
                  isChecked={linkedStatus === 'enabled'}
                  onChange={linkedStatusMap.onChange}
                />
              </CmFormControl>
            </CmFlex>
          </CmBox>
          <VerticalSeparate margin={'30px auto'} />
          <CmStack spacing="5px">
            <CmLabelWithQuestionIconAndInput
              isRequired
              id={PREFIX + masterStoreForm.name.key}
              isInvalid={error.name.isError}
              label={masterStoreForm.name.label}
              fontWeight="bold"
              value={masterStoreName}
              onChange={handleInputMasterStoreName}
              placeholder="海鮮居酒屋 日本橋堀留町店"
              question={masterStoreForm.name.question}
            >
              <CmFormErrorMessage>{error.name.message}</CmFormErrorMessage>
            </CmLabelWithQuestionIconAndInput>
            <CmText fontWeight="bold">{authStoreIdText}</CmText>
            <CmLabelWithQuestionIconAndSingleReactSelect
              fontWeight="bold"
              label={masterStoreForm.authStoreName.label}
              isRequired
              name={PREFIX + masterStoreForm.authStoreName.key}
              closeMenuOnSelect
              isClearable={false}
              singleSelectInput={authStoreSelectInput}
              question={masterStoreForm.authStoreName.question}
            />
            <CmBox />
            <CmLabelWithQuestionIconAndText
              label={masterStoreForm.associatebrandName.label}
              name={associateBrandSelectInput.value?.label ?? ''}
              question={masterStoreForm.associatebrandName.question}
            />
            <CmLabelAndInput
              id={PREFIX + masterStoreForm.businessNumber.key}
              label={masterStoreForm.businessNumber.label}
              isInvalid={error.businessNumber.isError}
              fontWeight="bold"
              value={masterStoreBusinessNumber}
              onChange={handleInputMasterStoreBusinessNumber}
              placeholder="T01234567890123"
            />
            <CmLabelWithQuestionIconAndInput
              id={PREFIX + masterStoreForm.businessName.key}
              label={masterStoreForm.businessName.label}
              isInvalid={error.businessName.isError}
              fontWeight="bold"
              value={masterStoreBusinessName}
              onChange={handleInputMasterStoreBusinessName}
              placeholder="インボイス用店舗名"
              question={masterStoreForm.businessName.question}
            >
              <CmFormErrorMessage>
                {error.businessName.message}
              </CmFormErrorMessage>
            </CmLabelWithQuestionIconAndInput>
            <CmBox>
              <CmFlex flexDirection="row-reverse">
                <CmButton
                  mt="10px"
                  onClick={() =>
                    handleUpdateStore({
                      name: masterStoreName,
                      auth_store_id: Number(
                        authStoreSelectInput.value?.value ?? 0
                      ),
                      business_number:
                        masterStoreBusinessNumber !== ''
                          ? masterStoreBusinessNumber
                          : null,
                      business_name:
                        masterStoreBusinessName !== ''
                          ? masterStoreBusinessName
                          : null,
                    })
                  }
                  isDisabled={false}
                >
                  更新
                </CmButton>
              </CmFlex>
            </CmBox>
          </CmStack>
          <CmBox my="30px" borderBottom="2px solid #bbb" height="0.5px" />
          <CmBox>
            <CmHeading size="md">デリバリーサービス連携</CmHeading>
            <CmBox ml="20px" mt="20px">
              {hasCamelOrderSite && (
                <>
                  <CmBox>
                    <TitleLine
                      deliveryStore={camelOrderDeliveryStore}
                      deliveryServiceName="CamelOrder"
                      deliveryServiceDisplayName="CamelOrder"
                      {...{ handleOpenModalAndSetDeliveryStore }}
                    />
                  </CmBox>
                  <CmFlex
                    flexDirection="column"
                    mt="20px"
                    ml="20px"
                    alignItems="stretch"
                  >
                    <CmLabelAndSelect
                      isRequired
                      label="調理時間"
                      id={PREFIX + 'camel-order-cooking-time'}
                      onChange={handleChangeCamelOrderCookingTime}
                      width="200px"
                      fontWeight="bold"
                      value={camelOrderDeliveryStore.cooking_time ?? ''}
                    >
                      <option
                        value=""
                        hidden={camelOrderDeliveryStore.id !== ''}
                      >
                        選択してください
                      </option>
                      {COOKING_TIMES_BY_CAMEL_ORDER.map((time) => (
                        <option key={time} value={time}>
                          {time}分
                        </option>
                      ))}
                    </CmLabelAndSelect>
                    <CmBox mt="20px">
                      <CmHeading fontSize="16px" pb="10px" isRequired>
                        対応注文方式
                      </CmHeading>
                      <CmBox pl="20px">
                        <CmHelpterText mb="5px">
                          ※最低一つを選択して下さい。
                        </CmHelpterText>
                        <CmStack spacing="15px">
                          {orderMethods.map((option, index) => (
                            <CmCheckBox
                              key={index}
                              ml={index > 0 ? '20px' : undefined}
                              isChecked={
                                camelOrderDeliveryStore.camel_order_prop?.order_methods?.includes(
                                  option.value
                                ) ?? false
                              }
                              value={option.value}
                              onChange={handleChangeOrderMethod}
                              isDisabled={
                                !isAvailableEatin && option.value === 'EAT_IN'
                              }
                            >
                              {option.name}
                            </CmCheckBox>
                          ))}
                        </CmStack>
                      </CmBox>
                    </CmBox>
                    <CmBox mt="20px">
                      <CmHeading fontSize="16px" pb="10px">
                        対応配達方式
                      </CmHeading>
                      <CmBox pl="20px">
                        <CmHelpterText mb="5px">
                          ※デリバリーを選択した場合のみ、いずれかの配達方法を選択してください。
                        </CmHelpterText>
                        <CmRadioGroup>
                          <CmStack direction="column" spacing="15px">
                            {deliveryMethods.map((option, index) => (
                              <CmCheckBox
                                isDisabled={isDisabledCamelOrderDeliveryMethodCheckButton(
                                  option.value
                                )}
                                key={index}
                                ml={index > 0 ? '20px' : undefined}
                                isChecked={isDeliveryMethodChecked(
                                  option.value
                                )}
                                value={option.value}
                                onChange={handleChangeDeliveryMethod}
                              >
                                {option.name}
                              </CmCheckBox>
                            ))}
                          </CmStack>
                        </CmRadioGroup>

                        <div>
                          {isDeliveryAreaEmpty ? (
                            <CmHelpterText mt="15px">
                              ※自社配達機能は、自社配達設定で配達エリアの設定後に選択可能になります。
                            </CmHelpterText>
                          ) : (
                            <div
                              style={{
                                backgroundColor: '#cce5ff',
                                marginTop: '10px',
                                padding: '10px',
                                fontSize: '14px',
                              }}
                            >
                              <div>
                                <p style={{ fontWeight: 'bold' }}>
                                  自社配達エリア
                                </p>
                                <div>
                                  {deliveryAreaNames(
                                    targetMasterStore?.delivery_areas
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                          <div style={{ fontSize: '14px', marginTop: '10px' }}>
                            <CmNextLink to={`/delivery/in-house`}>
                              自社配達設定
                            </CmNextLink>
                          </div>
                        </div>
                      </CmBox>
                    </CmBox>
                    <CmBox mt="10px">
                      <CmStack mt="5px" alignSelf="flex-start">
                        <CmFormControl>
                          <CmFormLabel fontWeight="bold">
                            テイクアウト受け取り可能期間
                          </CmFormLabel>
                          <CmFlex align="center">
                            <CmSelect
                              isDisabled={!isTakeoutOrderMethodSelected}
                              value={
                                camelOrderDeliveryStore.camel_order_prop
                                  ?.takeout_start_days_offset ??
                                TAKEOUT_START_DAYS_OFFSET_DEFAULT
                              }
                              isRequired
                              onChange={handleChangeTakoutStartDaysOffset}
                            >
                              {camelOrderDateOffsetOptions}
                            </CmSelect>
                            <CmText mx={2}>〜</CmText>
                            <CmSelect
                              isDisabled={!isTakeoutOrderMethodSelected}
                              value={
                                camelOrderDeliveryStore.camel_order_prop
                                  ?.takeout_end_days_offset ??
                                TAKEOUT_END_DAYS_OFFSET_DEFAULT
                              }
                              isRequired
                              onChange={handleChangeTakoutEndDaysOffset}
                            >
                              {camelOrderDateOffsetOptions}
                            </CmSelect>
                          </CmFlex>
                        </CmFormControl>
                      </CmStack>
                    </CmBox>
                    <CmBox mt="10px">
                      <CmFormControl as="fieldset">
                        <CmFormLabel as="legend" fontWeight="bold">
                          テイクアウトの受け取り日時設定
                        </CmFormLabel>
                        <CmStack spacing="5px" pl="20px">
                          <CmRadioGroup
                            value={isAsapDisplayed}
                            onChange={(value) =>
                              handleChangeCamelOrderProp(
                                'is_asap_displayed',
                                value === 'asap'
                              )
                            }
                          >
                            <CmStack direction="column" spacing="5px">
                              <CmRadio
                                value="scheduled"
                                isDisabled={!isTakeoutOrderMethodSelected}
                              >
                                日時指定のみ
                              </CmRadio>
                              <CmRadio
                                value="asap"
                                isDisabled={!isTakeoutOrderMethodSelected}
                              >
                                日時指定＋最短受け取り
                              </CmRadio>
                            </CmStack>
                          </CmRadioGroup>
                        </CmStack>
                      </CmFormControl>
                    </CmBox>

                    <CmBox mt="10px">
                      <CmLabelAndTextarea
                        id={PREFIX + 'uberEmail'}
                        isRequired={false}
                        isDisabled={
                          !camelOrderDeliveryStore.camel_order_prop?.delivery_methods?.includes(
                            DELIVERY_METHOD.DELIVERY_BY_SERVICE
                          )
                        }
                        label={'配達員への商品受け渡し時の注意事項'}
                        fontWeight="bold"
                        value={
                          camelOrderDeliveryStore.camel_order_prop
                            ?.pickup_notes ?? ''
                        }
                        isInvalid={pickupNotesError !== ''}
                        onChange={handleChangePickupNotes}
                        error={
                          <CmFormErrorMessage>
                            {pickupNotesError}
                          </CmFormErrorMessage>
                        }
                      />
                      <CmHelpterText>
                        ※200文字以内で入力してください
                      </CmHelpterText>
                    </CmBox>

                    {isEatInOrderMethodSelected && (
                      <CmBox mt="20px">
                        <CmHeading fontSize="16px" pb="10px">
                          店内飲食設定
                        </CmHeading>
                        <CmBox pl="20px">
                          <CmFormLabel fontWeight="bold">
                            ご利用方法
                          </CmFormLabel>
                          <CmStack spacing="15px" mt="5px">
                            <CmCheckBox
                              isChecked={isCounterPickupChecked}
                              value="COUNTER_PICKUP"
                              onChange={handleChangeEatInReceiveMethod}
                            >
                              カウンターで受け取り
                            </CmCheckBox>
                            <CmCheckBox
                              isChecked={isTableDeliveryChecked}
                              value="TABLE_DELIVERY"
                              onChange={handleChangeEatInReceiveMethod}
                            >
                              テーブルにスタッフがお届け
                            </CmCheckBox>
                          </CmStack>
                          {isTableDeliveryChecked && (
                            <CmBox mt="10px">
                              <CmLabelAndInput
                                id={PREFIX + 'eat-in-table-no-placeholder'}
                                label={'プレースホルダー文言'}
                                fontWeight="bold"
                                isInvalid={tableNoPlaceholderError !== ''}
                                value={
                                  camelOrderDeliveryStore.camel_order_prop
                                    ?.eat_in_settings?.table_no_placeholder ??
                                  ''
                                }
                                onChange={handleChangeTableNoPlaceholder}
                              >
                                <CmHelpterText>
                                  ※20文字以内で入力してください
                                </CmHelpterText>
                                <CmFormErrorMessage>
                                  {tableNoPlaceholderError}
                                </CmFormErrorMessage>
                              </CmLabelAndInput>
                            </CmBox>
                          )}
                        </CmBox>
                      </CmBox>
                    )}

                    <CmFlex justifyContent="flex-end">
                      {camelOrderDeliveryStore.id === '' ? (
                        <CmButton mt="20px" onClick={camelOrderHandler.create}>
                          登録
                        </CmButton>
                      ) : (
                        <CmButton
                          mt="20px"
                          onClick={camelOrderHandler.update}
                          isDisabled={isDisabledCamelOrderDeliveryStoreUpdateButton()}
                        >
                          更新
                        </CmButton>
                      )}
                    </CmFlex>
                  </CmFlex>
                  <VerticalSeparate margin={'30px auto'} />
                </>
              )}
              {hasCamelCall && (
                <>
                  <CmBox>
                    <TitleLine
                      deliveryStore={camelCallDeliveryStore}
                      deliveryServiceName="CamelCall"
                      deliveryServiceDisplayName="CamelCall"
                      {...{ handleOpenModalAndSetDeliveryStore }}
                    />
                  </CmBox>
                  <CmFlex
                    flexDirection="column"
                    mt="20px"
                    ml="20px"
                    alignItems="stretch"
                  >
                    <CmLabelAndSelect
                      isRequired
                      label="調理時間"
                      id={PREFIX + 'camel-call-cooking-time'}
                      onChange={handleChangeCamelCallCookingTime}
                      width="200px"
                      fontWeight="bold"
                      value={camelCallDeliveryStore.cooking_time ?? ''}
                    >
                      <option
                        value=""
                        hidden={camelCallDeliveryStore.id !== ''}
                      >
                        選択してください
                      </option>
                      {COOKING_TIMES_BY_CAMEL_CALL.map((time) => (
                        <option key={time} value={time}>
                          {time}分
                        </option>
                      ))}
                    </CmLabelAndSelect>
                    <CmBox mt="20px">
                      <CmHeading fontSize="16px" pb="10px" isRequired>
                        対応注文方式
                      </CmHeading>
                      <CmBox pl="20px">
                        <CmStack spacing="15px">
                          <CmCheckBox
                            isChecked={
                              camelCallDeliveryStore.camel_order_prop?.order_methods?.includes(
                                ORDER_METHOD.TAKE_OUT
                              ) ?? false
                            }
                            value={ORDER_METHOD.TAKE_OUT}
                            onChange={handleChangeCamelCallOrderMethod}
                          >
                            {orderMethod.TAKE_OUT}
                          </CmCheckBox>
                          <CmCheckBox
                            isChecked={
                              camelCallDeliveryStore.camel_order_prop?.order_methods?.includes(
                                ORDER_METHOD.DELIVERY
                              ) ?? false
                            }
                            value={ORDER_METHOD.DELIVERY}
                            onChange={handleChangeCamelCallOrderMethod}
                            isDisabled={isDeliveryAreaEmpty}
                          >
                            {orderMethod.DELIVERY}
                          </CmCheckBox>
                        </CmStack>
                      </CmBox>
                    </CmBox>
                    <CmBox mt="20px">
                      <CmHeading fontSize="16px" pb="10px">
                        対応配達方式
                      </CmHeading>
                      <CmBox pl="20px">
                        <CmRadioGroup>
                          <CmStack direction="column" spacing="15px">
                            <CmCheckBox
                              isDisabled={isDisabledCamelCallDeliveryMethodCheckButton(
                                DELIVERY_METHOD.DELIVERY_BY_STORE
                              )}
                              isChecked={
                                camelCallDeliveryStore.camel_order_prop?.delivery_methods?.includes(
                                  DELIVERY_METHOD.DELIVERY_BY_STORE
                                ) ?? false
                              }
                              value={DELIVERY_METHOD.DELIVERY_BY_STORE}
                              onChange={handleChangeCamelCallDeliveryMethod}
                            >
                              {deliveryMethod.DELIVERY_BY_STORE}
                            </CmCheckBox>
                          </CmStack>
                        </CmRadioGroup>
                        <div>
                          {isDeliveryAreaEmpty ? (
                            <CmHelpterText mt="15px">
                              ※自社配達機能は、自社配達設定で配達エリアの設定後に選択可能になります。
                            </CmHelpterText>
                          ) : (
                            <div
                              style={{
                                backgroundColor: '#cce5ff',
                                marginTop: '10px',
                                padding: '10px',
                                fontSize: '14px',
                              }}
                            >
                              <div>
                                <p style={{ fontWeight: 'bold' }}>
                                  自社配達エリア
                                </p>
                                <div>
                                  {deliveryAreaNames(
                                    targetMasterStore?.delivery_areas
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                          <div style={{ fontSize: '14px', marginTop: '10px' }}>
                            <CmNextLink to={`/delivery/in-house`}>
                              自社配達設定
                            </CmNextLink>
                          </div>
                        </div>
                      </CmBox>
                    </CmBox>
                    <CmFlex justifyContent="flex-end">
                      {camelCallDeliveryStore.id === '' ? (
                        <CmButton mt="20px" onClick={camelCallHandler.create}>
                          登録
                        </CmButton>
                      ) : (
                        <CmButton
                          mt="20px"
                          onClick={camelCallHandler.update}
                          isDisabled={isDisabledCamelCallDeliveryStoreUpdateButton()}
                        >
                          更新
                        </CmButton>
                      )}
                    </CmFlex>
                  </CmFlex>
                  <VerticalSeparate margin={'30px auto'} />
                </>
              )}
              <CmBox>
                <TitleLine
                  deliveryStore={uberDeliveryStore}
                  deliveryServiceName="Uber Eats"
                  deliveryServiceDisplayName="Uber Eats"
                  {...{ handleOpenModalAndSetDeliveryStore }}
                />
                <CmBox ml="20px">
                  <CmBox mt="5px">
                    {/* DeliveryStore未作成の場合はUber店舗選択プルダウン表示 */}
                    {uberDeliveryStore.id === '' && (
                      <CmLabelAndSingleReactSelect
                        label="Uber Eats出店店舗"
                        name="uberEatsStore"
                        fontWeight="bold"
                        closeMenuOnSelect
                        isClearable={false}
                        singleSelectInput={
                          uberIntegrationReadyDeliveryStoreSelectInput
                        }
                        isRequired
                      >
                        <CmHelpterText mt="3px">
                          ※ 「Uber
                          Eats店舗認証」ページで認証を行ったUberEats店舗を選択してください。
                        </CmHelpterText>
                        <CmHelpterText mt="3px">
                          ※ 選択したい店舗が表示されない場合は、
                          「店舗一覧」ページ設置の「Uber
                          Eats一括認証」より認証を行ってください。
                        </CmHelpterText>
                      </CmLabelAndSingleReactSelect>
                    )}
                    {/* DeliveryStore作成済の場合はUberEats店舗名を表示 */}
                    {uberDeliveryStore.id !== '' &&
                      uberDeliveryStore.external_store_name !== null && (
                        <CmLabelAndText
                          label="連携済Uber Eats出店店舗"
                          name={uberDeliveryStore.external_store_name}
                        />
                      )}
                    {/*
                          DeliveryStore作成済だがExternalStoreNameが存在しない場合は非表示
                          Uber一括認証方式変更前にDeliveryStoreが作成されてケースで、参照するIntegrationReadyDeliveryStoreが存在しないため
                      */}
                    {uberDeliveryStore.id !== '' &&
                      uberDeliveryStore.external_store_name === null && <></>}
                  </CmBox>
                  <CmBox mt="5px">
                    <CmLabelAndInput
                      id={PREFIX + 'uberEmail'}
                      isRequired
                      type="email"
                      label={'ユーザー名（メールアドレス）'}
                      fontWeight="bold"
                      value={
                        uberDeliveryStore.external_auth_delivery_store?.email
                      }
                      onChange={handleInputUberEmail}
                    >
                      <CmHelpterText mt="3px">
                        Uber
                        Eatsオーダーズ（注文受注用アプリ）のログイン情報をご記入ください。
                      </CmHelpterText>
                    </CmLabelAndInput>
                  </CmBox>
                  <CmBox mt="5px">
                    <CmLabelAndInput
                      label={'パスワード'}
                      id={PREFIX + 'uberPassword'}
                      isRequired
                      isPassword
                      fontWeight="bold"
                      value={
                        uberDeliveryStore.external_auth_delivery_store?.password
                      }
                      onChange={handleInputUberPassword}
                    />
                  </CmBox>
                  <CmFlex flexDirection="row-reverse">
                    {uberDeliveryStore.id === '' ? (
                      <CmButton mt="20px" onClick={uberHandler.create}>
                        登録
                      </CmButton>
                    ) : (
                      <CmButton mt="20px" onClick={uberHandler.update}>
                        更新
                      </CmButton>
                    )}
                  </CmFlex>
                </CmBox>
              </CmBox>
              {menuDeliveryStore.id !== '' && (
                <>
                  <VerticalSeparate margin={'30px auto'} />
                  <CmBox>
                    <TitleLine
                      deliveryStore={menuDeliveryStore}
                      deliveryServiceName="menu"
                      deliveryServiceDisplayName="menu"
                      {...{ handleOpenModalAndSetDeliveryStore }}
                    />
                    <CmBox ml="20px">
                      <CmBox mt="5px">
                        <CmLabelAndInput
                          id={PREFIX + 'menuStoreId'}
                          isRequired
                          label={'menuストアID'}
                          fontWeight="bold"
                          value={menuDeliveryStore.external_store_id}
                          onChange={handleInputMenuStoreId}
                        >
                          <CmHelpterText>
                            半角数字で入力してください。
                          </CmHelpterText>
                        </CmLabelAndInput>
                      </CmBox>
                      <CmFlex flexDirection="row-reverse">
                        <CmButton mt="20px" onClick={menuHandler.update}>
                          更新
                        </CmButton>
                      </CmFlex>
                    </CmBox>
                  </CmBox>
                </>
              )}
              <VerticalSeparate margin={'30px auto'} />
              <CmBox>
                <TitleLine
                  deliveryStore={demaecanDeliveryStore}
                  deliveryServiceName="Demaecan"
                  deliveryServiceDisplayName="出前館"
                  {...{ handleOpenModalAndSetDeliveryStore }}
                />
                <CmFlex>
                  <div
                    style={{
                      marginTop: '20px',
                      marginRight: '10px',
                      alignSelf: 'center',
                      fontSize: '15px',
                    }}
                  >
                    <span>【連携フロー】</span>
                    <br />
                    <span>
                      ①Camelとの連携を希望される場合は、必ず出前館社に
                    </span>
                    <br />
                    <span>&emsp;Camel連携希望の旨をご連絡ください</span>
                    <br />
                    <br />
                    <span>
                      ②出前館社との連携開始日の確定後、「登録」をクリックし、
                    </span>
                    <br />
                    <span>&emsp;連携日をご登録ください</span>
                  </div>
                </CmFlex>
                <CmFlex flexDirection="row-reverse">
                  <CmButton
                    isDisabled={demaecanDeliveryStore.id !== ''}
                    mt="20px"
                    onClick={demaeCanHandler.create}
                  >
                    登録
                  </CmButton>
                </CmFlex>
              </CmBox>
            </CmBox>
          </CmBox>
        </CmBox>
        <DeliveryStoreIntegrationModal
          {...{
            targetDeliveryStore: targetDeliveryStoreDeliveryService,
            isOpen: isOpenIntegrationModal,
            onClose: handelCloseIntegrationModal,
            integrationDate,
            handleInputIntegrationDate,
            handleChangeTime,
            handleCreateIntegration,
            text: `${
              targetDeliveryStore.deliveryServiceName + targetDeliveryStore.text
            }`,
          }}
        >
          {targetDeliveryStore.store.integration_status === noRequest ? (
            // @ts-ignore TS2590
            <CmButton onClick={handleCreateIntegration}>登録</CmButton>
          ) : (
            <CmButton
              isDisabled={isDisabledIntegrationUpdateButton}
              onClick={handleUpdateIntegration}
            >
              更新
            </CmButton>
          )}
        </DeliveryStoreIntegrationModal>
      </SidebarWrapper>
      {isLoading && <CmLongLoading />}
    </>
  );
};
